/**
 * RulesHelperPanel — collapsible right-rail rules reference. Pulls
 * content from `apps/web/src/data/rules-helper.json` (generated by
 * `scripts/build-rules-helper.mjs` from the canonical
 * `skills/wonders-rules-v1/SKILL.md`). Sections §1–§7 cover the
 * player-facing material: turn flow, resources, card types, orbital
 * links, battle resolution, zones, keywords.
 *
 * Each section is collapsible; the panel itself is collapsible too
 * (header click toggles). Renders the markdown body verbatim (no
 * MD-to-HTML conversion — the source is plain prose with bullet
 * lists that read fine in a `<pre>`-style whitespace-preserving
 * block).
 */

import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import rulesData from '../../data/rules-helper.json';

type Section = { number: number; title: string; body: string };
type RulesData = { sections: readonly Section[] };

const data = rulesData as RulesData;

export function RulesHelperPanel(): ReactElement {
  const [panelOpen, setPanelOpen] = useState(false);
  const [openSection, setOpenSection] = useState<number | null>(null);

  return (
    <div className="flex flex-col border-cosmos-800 border-b">
      <button
        type="button"
        onClick={() => setPanelOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 text-cosmos-200 text-sm transition hover:bg-cosmos-800"
      >
        <BookOpen className="h-3.5 w-3.5 text-amber-300" aria-hidden="true" />
        <span className="font-mono text-[11px] uppercase tracking-wide">Rules</span>
        <span className="ml-auto text-cosmos-500 text-[10px]">{panelOpen ? 'hide' : 'show'}</span>
      </button>
      {panelOpen && (
        <div className="max-h-[42vh] overflow-y-auto bg-cosmos-950/40 px-2 py-1">
          {data.sections.map((s) => {
            const isOpen = openSection === s.number;
            return (
              <div key={s.number} className="border-cosmos-900 border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? null : s.number)}
                  className="flex w-full items-center gap-1 px-2 py-1.5 text-left text-cosmos-200 text-xs transition hover:bg-cosmos-800/60"
                >
                  {isOpen ? (
                    <ChevronDown className="h-3 w-3 text-cosmos-400" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-cosmos-400" aria-hidden="true" />
                  )}
                  <span className="font-mono text-cosmos-500 text-[10px]">§{s.number}</span>
                  <span className="font-semibold">{s.title}</span>
                </button>
                {isOpen && (
                  <pre className="whitespace-pre-wrap break-words px-3 py-2 font-mono text-[11px] text-cosmos-300 leading-relaxed">
                    {s.body}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
